import { useEffect, useRef, useState } from "react";
import { FileText, X, Loader2, FileUp, ExternalLink } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function asUrl(u) {
  if (!u) return "";
  return u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

/**
 * Single-PDF uploader — POST /api/uploads-api/pdf (admin only).
 * Cross-browser reliable: visible button → ref.click() → real file picker.
 * `onBusy(bool)` lets parent disable Save while uploading.
 */
export default function AdminPdfUploader({ value, onChange, label = "PDF File", testId = "pdf", onBusy }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    onBusy?.(busy);
  }, [busy, onBusy]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a .pdf file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads-api/pdf", fd);
      onChange(data.url);
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) upload(f);
  };

  const clear = () => onChange("");
  const shortName = value ? value.split("/").pop().replace(/^[a-f0-9-]{20,}_/, "") : "";

  return (
    <div data-testid={`${testId}-uploader`}>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-2">{label}</div>

      {value ? (
        <div className="border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-bold text-carbon truncate" title={shortName}>
                {shortName || "document.pdf"}
              </div>
              <a
                href={asUrl(value)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-amber-600 hover:text-amber-700 inline-flex items-center gap-1"
                data-testid={`${testId}-view-link`}
              >
                View PDF <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => !busy && inputRef.current?.click()}
              disabled={busy}
              data-testid={`${testId}-replace-btn`}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-slate-300 hover:bg-white disabled:opacity-50"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={clear}
              data-testid={`${testId}-remove-btn`}
              className="w-8 h-8 grid place-items-center text-red-600 hover:bg-red-50"
              aria-label="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => !busy && inputRef.current?.click()}
          data-testid={`${testId}-upload-btn`}
          className={`block w-full border-2 border-dashed px-4 py-6 text-center transition-colors ${
            busy
              ? "border-slate-200 text-slate-400 cursor-not-allowed"
              : "border-slate-300 hover:border-amber-500 hover:bg-amber-50/30 text-slate-500 hover:text-amber-600 cursor-pointer"
          }`}
        >
          <div className="flex flex-col items-center gap-1.5">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <FileUp className="w-6 h-6" />}
            <span className="text-[11px] uppercase tracking-wider font-bold">
              {busy ? "Uploading…" : "Upload PDF"}
            </span>
            <span className="text-[10px] text-slate-400 normal-case font-normal">
              Brochure / Datasheet · PDF only · ≤ 25 MB
            </span>
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={onPick}
        data-testid={`${testId}-file-input`}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
    </div>
  );
}
